import {useEffect, useState} from "react";
import {Outlet, useLoaderData, useLocation, useNavigate, useParams, useRevalidator} from "react-router-dom";
import {ActivityCategoryInfo, ActivityMatchInfo} from "../../interface";
import {Segmented, Typography, Modal, Select} from "antd";
import { LockOutlined, ExclamationCircleOutlined } from "@ant-design/icons";
import "./editLayout.css";

const { Title } = Typography;

const EditActivityLayout: React.FC = () => {
    const {id} = useParams()
    const data = useLoaderData() as {
        "activity": ActivityMatchInfo, 
        "categories": ActivityCategoryInfo[] | null,
        "rtds_key": string | null
    }
    const revalidator = useRevalidator();

    const navigate = useNavigate()
    const location = useLocation();
    const { pathname } = location;

    const [navLinks, setNavLinks] = useState<any>([])
    const [navValue, setNavValue] = useState("")
    const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false)

    const reloadData = (newAction: string = "") => {
        revalidator.revalidate();

        if (newAction !== "") {
            setNavValue(newAction);
            navigate(`/activity/${id}/${newAction}`, {
                replace: true
            });
        }
    };

    useEffect(() => {
        let links = [];
        if (!data.categories || data.categories.length < 1) {
            links = [
                {label: "Info", value: "info", locked: false},
            ]
        } else {
            links = [
                {label: "Info", value: "info", locked: false},
                {label: "Participants", value: "participants", locked: false},
                {label: "Category", value: "category", locked: false},
                {label: "Schedule", value: "schedule", locked: false},
                {label: "Bracket", value: "bracket", locked: false},
                {label: "Download", value: "download", locked: false},
                {label: "RTDS", value: "rtds", locked: false}
            ]
        }

        let currentPath = pathname.split('/')[3] || '';
        if (currentPath === "matches") {
            currentPath = "bracket"
        }
        if (currentPath === "bracket-builder") {
            currentPath = "category"
        }

        const matchedLink = links.find(link => link.value.toLowerCase() === currentPath.toLowerCase());
        
        if (matchedLink) {
            setNavValue(matchedLink.value);
        } else {
            setNavValue('info');
        }

        setNavLinks(links)
    }, [pathname, data.categories])

    // Add beforeunload event handler to detect page refresh/close
    useEffect(() => {
        const handleBeforeUnload = (event: BeforeUnloadEvent) => {
            if (hasUnsavedChanges) {
                event.preventDefault();
                event.returnValue = 'You have unsaved changes. Are you sure you want to leave?';
                return 'You have unsaved changes. Are you sure you want to leave?';
            }
        };

        window.addEventListener('beforeunload', handleBeforeUnload);

        return () => {
            window.removeEventListener('beforeunload', handleBeforeUnload);
        };
    }, [hasUnsavedChanges])

    const changeNav = (value: string) => {
        const selectedLink = navLinks.find((link: any) => link.value === value);
        if (selectedLink?.locked) {
            return;
        }
        
        // Check for unsaved changes before navigation
        if (hasUnsavedChanges) {
            Modal.confirm({
                title: 'Unsaved Changes',
                icon: <ExclamationCircleOutlined />,
                content: 'You have unsaved changes. Are you sure you want to continue navigating? Your changes will be lost.',
                okText: 'Continue',
                cancelText: 'Stay',
                onOk: () => {
                    setHasUnsavedChanges(false);
                    proceedWithNavigation(value);
                },
                onCancel: () => {
                    // Do nothing, stay on current page
                }
            });
            return;
        }
        
        proceedWithNavigation(value);
    }
    
    const proceedWithNavigation = (value: string) => {
        //console.info("update value", value)
        if (value === 'rtds') {
            openRTDS();
            return ;
        }

        setNavValue(value)
        navigate(`/activity/${id}/${value}`);
    }

    const openRTDS = () => {
        window.open(`/web/rtds?activity_id=${data.activity.id}&key=${data.rtds_key}`)
    }

    const renderNavLabel = (option: any) => {
        return (
            <span>
                {option.label}
                {option.locked && <LockOutlined style={{ marginLeft: 4 }} />}
            </span>
        );
    };

    return <>
        <Title level={4}>{ data.activity.name }</Title>
        <nav className="activity-edit-nav-shell" aria-label="Activity sections">
            <div className="activity-edit-nav-mobile">
                <Select
                    aria-label="Activity section"
                    value={navValue || undefined}
                    placeholder="Select a section"
                    size="large"
                    variant="borderless"
                    virtual={false}
                    options={navLinks.map((link: any) => ({
                        value: link.value,
                        label: renderNavLabel(link),
                        disabled: link.locked,
                    }))}
                    onChange={changeNav}
                />
            </div>
            <div className="activity-edit-nav-desktop">
                <Segmented
                    className="activity-edit-nav"
                    value={navValue}
                    options={navLinks.map((link: any) => ({
                        ...link,
                        label: renderNavLabel(link)
                    }))}
                    onChange={changeNav}
                    block
                />
            </div>
        </nav>

        <Outlet
            context={{
               ...data,
                reloadData,
                hasUnsavedChanges,
                setHasUnsavedChanges
            }}
        />
    </>
}

export default EditActivityLayout
